<template>
  <Modal
    :title="editModalTitle"
    bottom-close-button-label="保存"
    :isCloseButtonDisabled="isSaveButtonDisabled"
    @close="save"
    @dismiss="$emit('close')"
  >
    <template v-slot:body>
      <div>
        <div class="form-group">
          <label class="form-control-label">動画タグ</label>
          <input type="text" class="form-control" v-model="state.tag.tagName" />
          <div class="warning-text-wrap mt8 ml4" v-if="isTagNameTooLong">
            <span class="text-warning">※{{tagNameTooLongMsg}}</span>
          </div>
        </div>
        <div class="form-group" style="margin-top:32px;">
          <div class="custom-control custom-checkbox show-edit-detail">
            <input
              type="checkbox"
              class="custom-control-input"
              id="show-tag-edit-detail"
              v-model="state.showEditDetail"
            >
            <label class="custom-control-label" for="show-tag-edit-detail">
              詳細項目を表示する
            </label>
          </div>
        </div>
        <div class="form-group" v-show="state.showEditDetail">
          <label class="form-control-label">事業所</label>
          <select class="form-control" v-model="state.tag.group2Id" @change="state.tag.group3Id = null">
            <option v-for="e in groups2" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group" v-show="state.showEditDetail">
          <label class="form-control-label group2">部署</label>
          <select class="form-control" v-model="state.tag.group3Id">
            <option :value="null"></option>
            <option v-for="e in filteredGroups3" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group errors">
          <div class="error" v-for="(e, i) in httpErrorMsgs" :key="i">
            {{e}}
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useEditModalCommon } from 'src/composables/useEditModalCommon';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { AdminTagCreateParams, AdminTagUpdateParams } from 'src/models/apis/tag/adminTagRequest';
import type { TagInputModel } from 'src/models/tag';
import { createTagInitInputModel } from 'src/models/tag';

const props = defineProps<{
  isEditMode: boolean;
  tag: TagInputModel;
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  isRequesting: boolean;
  httpErrorMsgs: string[];
}>();

const emit = defineEmits<{
  (e: 'create', createData: AdminTagCreateParams): void;
  (e: 'update', id: number, updateData: AdminTagUpdateParams): void;
  (e: 'close'): void;
}>();

const state = reactive<{ tag: TagInputModel; showEditDetail: boolean }>({
  tag: createTagInitInputModel(),
  showEditDetail: false,
});

const { editModalTitle } = useEditModalCommon(props, () => props.tag, (copied) => {
  state.tag = copied;
  state.showEditDetail = false;
});

const tagNameTooLongMsg = '動画タグは全角16文字以内で入力してください';

// 半角は1文字、全角は2文字として計算
const halfWidthLetterCount = (str: string): number => {
  let ret = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    const asciiCode = str.charCodeAt(i);
    ret += asciiCode < 128 ? 1 : 2;
  }
  return ret;
};

const isTagNameTooLong = computed<boolean>(() => {
  return halfWidthLetterCount(state.tag.tagName) > 32;
});

const filteredGroups3 = computed<AdminGroup3[]>(() => {
  return props.groups3.filter(group3 => group3.parentId === state.tag.group2Id);
});

const validateTagForm = (tag: TagInputModel): boolean => {
  if (tag.tagName.length <= 0) return false;
  if (isTagNameTooLong.value) return false;
  if (!Number.isInteger(tag.group2Id)) return false;
  if (!Number.isInteger(tag.group3Id)) return false;

  return true;
};

const isSaveButtonDisabled = computed<boolean>(() => {
  return props.isRequesting || !validateTagForm(state.tag);
});

const createTag = (): void => {
  if (isSaveButtonDisabled.value) return;

  emit('create', {
    id: 0,
    tag_name: state.tag.tagName,
    group2_id: state.tag.group2Id ?? 0,
    group3_id: state.tag.group3Id ?? 0,
  });
};

const updateTag = (): void => {
  if (isSaveButtonDisabled.value || state.tag.id === null) return;

  emit('update', state.tag.id, {
    id: state.tag.id,
    tag_name: state.tag.tagName,
    group2_id: state.tag.group2Id ?? 0,
    group3_id: state.tag.group3Id ?? 0,
  });
};

const save = (): void => {
  if (props.isEditMode) {
    updateTag();
  } else {
    createTag();
  }
};
</script>

<style lang="scss" scoped>
.warning-text-wrap {
  font-size: 0.8rem;
}

.show-edit-detail {
  font-size: 0.9rem;
}
</style>
